import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, CheckCircle, FileText, GraduationCap, Loader2, Printer, RefreshCw, RotateCcw, X } from 'lucide-react';
import { formatMoney } from '../../utils/formatMoney';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { API_BASE_URL } from '../config/api';
import ConfirmModal from './ConfirmModal';
import { useToast } from './Toast';
import { LoadFailed } from '../settings/instantSettings';
import { cancellationPrintWarning } from '../../utils/cancellationPrint';
import {
  ChipTone, Column, DANGER_GHOST_BTN, DataTable, DateRange, DateRangeValue, DetailPanel, EmptyState, FilterSelect,
  GHOST_BTN, KeyValue, KeyValueItem, ListPage, ListToolbar, Notice, PRIMARY_BTN, Pagination, Stat, Stats, StatusChip,
  presetRange,
} from './listPage';

/**
 * «Παραστατικά» στη διάταξη «Πίνακας» (νέα εμφάνιση, 14/10/2026). Δύο χρήσεις:
 *  - το παράθυρο του ταμείου (default export, Navigation → μενού): ίδιο σχήμα με τις «Κλήσεις» —
 *    παράθυρο με τίτλο, μέσα του λίστα (αναζήτηση στον server, τύπος/κατάσταση/περίοδος, σελίδες ανά 50)
 *    και συρτάρι με τα στοιχεία και την Επανεκτύπωση / Επαναποστολή / Ακύρωση· η κρυφή «Εκπαιδευτικές»
 *    (5 × Ανανέωση) μένει ως είχε.
 *  - τα κομμάτια που μοιράζεται με τη σελίδα του διαχειριστή (ECRFiscalDocuments): ετικέτες τύπου και
 *    κατάστασης, στήλες του πίνακα, στοιχεία του συρταριού.
 * Ίδια endpoints, παράμετροι, δικαιώματα και ενέργειες με πριν· άλλαξε μόνο η εμφάνιση.
 */

/** Valid invoice type codes for the document filter dropdown */
type DocumentFilterType = 'all' | '11.1' | '11.2' | '1.1' | '2.1' | '8.6' | '9.3' | '11.4' | '5.1';

const PAGE_SIZE = 50;

export interface FiscalDocument {
  id: number;
  store_id: number;
  order_id: number | null;
  /** Παραστατικά τραπεζιού: εδώ αντί για order_id (το backend τα επανεκτυπώνει από το MARK). */
  dine_in_session_id?: number | null;
  document_type_id: number;
  document_type_name: string;
  invoice_type_code: string;
  series: string;
  document_number: number;
  document_year: number;
  mark: string | null;
  oxygen_id: string | null;
  oxygen_uid: string | null;
  status: string;
  total_net_amount: number;
  total_vat_amount: number;
  total_gross_amount: number;
  customer_vat_number: string | null;
  customer_name: string | null;
  payment_method_name: string | null;
  created_at: string;
  error_message: string | null;
  error_details: string | null;
  failure_message: string | null;
  response_payload: any | null;
  retry_count: number | null;
  /** Ό,τι στάλθηκε στον πάροχο (jsonb → αντικείμενο· κατά περίπτωση και ως JSON κείμενο). */
  request_payload?: unknown;
  /** fiscal_document_types.is_cancellation, αν το στείλει το backend (η λίστα σήμερα δεν το στέλνει). */
  is_cancellation?: boolean | null;
  /** Το τραπέζι της απόδειξης/του τιμολογίου τραπεζιού (GET …/fiscal/documents, από 25/10/2026). */
  dine_in_table_number?: string | null;
}

/**
 * Το '8.6' το μοιράζονται ΤΡΙΑ διαφορετικά παραστατικά:
 *  - order:      ΔΕΛΤΙΟ ΠΑΡΑΓΓΕΛΙΑΣ (τύπος 9)
 *  - correction: ΔΕΛΤΙΟ ΔΙΟΡΘΩΣΗΣ — ίδιος τύπος 9, γραμμές με rec_type 7 (κέρασμα, αφαίρεση/μετακίνηση
 *                είδους, αλλαγή έκπτωσης λογαριασμού)
 *  - return:     ΔΕΛΤΙΟ ΕΠΙΣΤΡΟΦΗΣ ΠΑΡΑΓΓΕΛΙΑΣ (τύπος 10, is_cancellation)
 * Ως τις 9/10/2026 έγραφαν όλα «Δελτίο Παρ.»: η μετακίνηση είδους βγάζει διορθωτικό στο ένα τραπέζι
 * και νέο 8.6 στο άλλο, ίδιο ποσό, ίδιο δευτερόλεπτο — και ο ιδιοκτήτης (GENERIS) τα διάβαζε ως
 * διπλή αποστολή. Από 14/10/2026 η ίδια διάκριση και στη σελίδα του διαχειριστή.
 */
export type OrderNoteKind = 'order' | 'correction' | 'return';

interface OrderNoteInfo {
  kind: OrderNoteKind;
  table: string | null;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

/** Ποτέ δεν πετάει: χαλασμένο ή άγνωστο σχήμα → null. */
const readPayload = (raw: unknown): Record<string, unknown> | null => {
  if (typeof raw !== 'string') return asRecord(raw);
  try {
    return asRecord(JSON.parse(raw));
  } catch {
    return null;
  }
};

/** Χωρίς τόνους, κεφαλαία: «Δελτίο Επιστροφής» και «ΔΕΛΤΙΟ ΕΠΙΣΤΡΟΦΗΣ» συγκρίνονται ίδια. */
const foldGreek = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();

export const describeOrderNote = (doc: FiscalDocument): OrderNoteInfo | null => {
  if (doc.invoice_type_code !== '8.6') return null;
  const payload = readPayload(doc.request_payload);

  // Το backend γράφει String(tableNumber): χωρίς τραπέζι φτάνει και ως 'null'/'undefined'.
  const rawTable = asRecord(payload?.header)?.table_number;
  const tableText = rawTable == null ? '' : String(rawTable).trim();
  const table = tableText && tableText !== 'null' && tableText !== 'undefined' ? tableText : null;

  const isReturn = Number(doc.document_type_id) === 10
    || doc.is_cancellation === true
    || foldGreek(doc.document_type_name || '').includes('ΕΠΙΣΤΡΟΦ');
  if (isReturn) return { kind: 'return', table };

  const lines = payload?.lines;
  const isCorrection = Array.isArray(lines) && lines.some(line => Number(asRecord(line)?.rec_type) === 7);
  return { kind: isCorrection ? 'correction' : 'order', table };
};

/**
 * Το τραπέζι του παραστατικού: του 8.6 από ό,τι στάλθηκε, της απόδειξης/του τιμολογίου από τη συνεδρία. Ως 25/10/2026
 * η απόδειξη τραπεζιού δεν έλεγε πουθενά ποιο τραπέζι ήταν (Π9 GENERIS: «δεν τη βρίσκω στα παραστατικά»).
 */
export const documentTable = (doc: FiscalDocument): string | null => {
  const fromNote = describeOrderNote(doc)?.table;
  if (fromNote) return fromNote;
  const own = doc.dine_in_table_number == null ? '' : String(doc.dine_in_table_number).trim();
  return own || null;
};

/** Επαναποστολή: όσα δεν έφτασαν στον πάροχο / την ΑΑΔΕ. */
export const RETRYABLE_STATUSES = ['error', 'transmission_failure', 'validation_error', 'pending'];
/** Τα ακυρωτικά/πιστωτικά δεν ακυρώνονται ξανά. */
export const NON_CANCELLABLE_TYPES = ['11.4', '5.1'];

/** Ακύρωση: μόνο εκδομένο (MARK) και όχι ακυρωτικό — χωρίς MARK θα έμενε κενό στην αρίθμηση. */
export const isCancellable = (doc: FiscalDocument): boolean =>
  doc.status === 'success' && !!doc.mark && !NON_CANCELLABLE_TYPES.includes(doc.invoice_type_code);

// Ώρα Ελλάδας και 24ωρη, όπως οι Κλήσεις και τα Ζ/Χ (ως 14/10 ο browser έγραφε «09:59 μ.μ.»).
const ATHENS = 'Europe/Athens';

export const fiscalDate = (iso: string): string => new Date(iso).toLocaleDateString('el-GR', {
  timeZone: ATHENS, day: '2-digit', month: '2-digit', year: 'numeric',
});

export const fiscalTime = (iso: string, seconds = false): string => new Date(iso).toLocaleTimeString('el-GR', {
  timeZone: ATHENS, hour: '2-digit', minute: '2-digit', hourCycle: 'h23', ...(seconds ? { second: '2-digit' } : {}),
});

export const fiscalWhen = (iso: string, seconds = false): string => `${fiscalDate(iso)} · ${fiscalTime(iso, seconds)}`;

// Οι εκπαιδευτικές: μέρα/μήνας και ώρα, όπως πριν (χωρίς έτος — είναι πάντα της ημέρας).
const dayTime = (iso: string): string => `${new Date(iso).toLocaleDateString('el-GR', {
  timeZone: ATHENS, day: '2-digit', month: '2-digit',
})} · ${fiscalTime(iso)}`;

const STATUS_KEYS: Record<string, string> = {
  success: 'statusSuccess',
  error: 'statusError',
  pending: 'statusPending',
  cancelled: 'statusCancelled',
  transmission_failure: 'statusTransmissionFailure',
  validation_error: 'statusValidationError',
};

// Πράσινο/πορτοκαλί/κόκκινο ΜΟΝΟ για το νόημα (εκδόθηκε / εκκρεμεί / απέτυχε)· το κείμενο το λέει πάντα.
const STATUS_TONES: Record<string, ChipTone> = {
  success: 'ok',
  error: 'danger',
  validation_error: 'danger',
  pending: 'warn',
  transmission_failure: 'warn',
  cancelled: 'muted',
};

const TYPE_KEYS: Record<string, string> = {
  '11.1': 'typeReceipt',
  '11.2': 'typeServiceReceipt',
  '1.1': 'typeInvoice',
  '2.1': 'typeServiceInvoice',
  '11.4': 'typeCancellation',
  '5.1': 'typeCreditNote',
  '8.6': 'typeDeliveryNote',
  '9.3': 'typeDispatchNote',
};

export interface FiscalTypeInfo {
  label: string;
  tone: ChipTone;
}

/** Ετικέτες τύπου/κατάστασης — ΙΔΙΕΣ στο ταμείο και στον διαχειριστή. */
export function useFiscalLabels(): { statusLabel: (status: string) => string; typeInfo: (doc: FiscalDocument) => FiscalTypeInfo } {
  const { t } = useTranslation(['orders']);
  return useMemo(() => ({
    statusLabel: (status: string) => (STATUS_KEYS[status] ? t(`orders:fiscalViewer.${STATUS_KEYS[status]}`) : status),
    // Ο τύπος δεν είναι κατάσταση: ουδέτερος, εκτός από διορθωτικό (προσοχή) και ακυρωτικά/επιστροφές.
    typeInfo: (doc: FiscalDocument) => {
      const code = doc.invoice_type_code;
      const kind = describeOrderNote(doc)?.kind;
      if (kind === 'correction') return { label: t('orders:fiscalViewer.typeCorrectionNote'), tone: 'warn' };
      if (kind === 'return') return { label: t('orders:fiscalViewer.typeReturnNote'), tone: 'danger' };
      const key = TYPE_KEYS[code];
      return { label: key ? t(`orders:fiscalViewer.${key}`) : code, tone: NON_CANCELLABLE_TYPES.includes(code) ? 'danger' : 'muted' };
    },
  }), [t]);
}

export const FiscalTypeChip: React.FC<{ doc: FiscalDocument }> = ({ doc }) => {
  const { typeInfo } = useFiscalLabels();
  const info = typeInfo(doc);
  return <StatusChip tone={info.tone} title={doc.invoice_type_code}>{info.label}</StatusChip>;
};

export const FiscalStatusChip: React.FC<{ status: string }> = ({ status }) => {
  const { statusLabel } = useFiscalLabels();
  return <StatusChip tone={STATUS_TONES[status] ?? 'muted'}>{statusLabel(status)}</StatusChip>;
};

const numberOf = (doc: FiscalDocument): string => `${doc.series}/${doc.document_number}`;

/** Στήλες του πίνακα παραστατικών (ίδιες στο ταμείο και στον διαχειριστή). */
export function useFiscalDocumentColumns(): Column<FiscalDocument>[] {
  const { t } = useTranslation(['orders', 'fiscalDocuments']);
  return useMemo<Column<FiscalDocument>[]>(() => {
    const tableOf = (doc: FiscalDocument): string | null => {
      const table = documentTable(doc);
      return table ? t('orders:fiscalViewer.tableLabel', { table }) : null;
    };
    return [
      {
        key: 'number',
        header: t('fiscalDocuments:columns.number'),
        width: 'minmax(96px, 0.8fr)',
        stack: 'title',
        render: d => {
          const table = tableOf(d);
          return (
            <>
              <span className="block font-semibold tabular-nums">{numberOf(d)}</span>
              {table && <span className="block text-xs text-t3 whitespace-nowrap">{table}</span>}
            </>
          );
        },
        stackRender: d => {
          const table = tableOf(d);
          return table ? `${numberOf(d)} · ${table}` : numberOf(d);
        },
      },
      {
        key: 'type',
        header: t('fiscalDocuments:columns.type'),
        width: 'minmax(118px, 1fr)',
        render: d => <FiscalTypeChip doc={d} />,
      },
      {
        key: 'date',
        header: t('fiscalDocuments:columns.date'),
        width: 'minmax(142px, 1fr)',
        render: d => <span className="tabular-nums whitespace-nowrap">{fiscalWhen(d.created_at)}</span>,
        stackRender: d => fiscalWhen(d.created_at),
      },
      {
        key: 'customer',
        header: t('fiscalDocuments:columns.customer'),
        width: 'minmax(140px, 1.4fr)',
        hideBelow: 'medium',
        render: d => (d.customer_name ? (
          <>
            <span className="block truncate" title={d.customer_name}>{d.customer_name}</span>
            {d.customer_vat_number && (
              <span className="block text-xs text-t3 tabular-nums">{t('orders:fiscalViewer.vatLabel')} {d.customer_vat_number}</span>
            )}
          </>
        ) : <span className="text-t4">—</span>),
      },
      {
        key: 'mark',
        header: t('fiscalDocuments:columns.mark'),
        width: 'minmax(124px, 1fr)',
        hideBelow: 'medium',
        render: d => (d.mark
          ? <span className="block truncate font-mono text-xs text-t2" title={d.mark}>{d.mark}</span>
          : <span className="text-t4">—</span>),
      },
      {
        key: 'status',
        header: t('fiscalDocuments:columns.status'),
        width: 'minmax(124px, 1fr)',
        render: d => <FiscalStatusChip status={d.status} />,
      },
      {
        key: 'amount',
        header: t('fiscalDocuments:columns.amount'),
        width: '104px',
        align: 'right',
        stack: 'end',
        render: d => <span className="font-semibold">{formatMoney(Number(d.total_gross_amount))}</span>,
        stackRender: d => formatMoney(Number(d.total_gross_amount)),
      },
    ];
  }, [t]);
}

/** Όνομα της γραμμής για αναγνώστες οθόνης: «Τιμολόγιο 3/1, 06/10/2026 · 21:59». */
export function useFiscalRowLabel(): (doc: FiscalDocument) => string {
  const { t } = useTranslation(['fiscalDocuments']);
  const { typeInfo } = useFiscalLabels();
  return useCallback(
    (doc: FiscalDocument) => t('fiscalDocuments:rowLabel', { type: typeInfo(doc).label, number: numberOf(doc), when: fiscalWhen(doc.created_at) }),
    [t, typeInfo],
  );
}

/** Τίτλος συρταριού: «Τιμολόγιο 3/1». */
export function useFiscalTitle(): (doc: FiscalDocument) => string {
  const { typeInfo } = useFiscalLabels();
  return useCallback((doc: FiscalDocument) => `${typeInfo(doc).label} ${numberOf(doc)}`, [typeInfo]);
}

const MONO = 'font-mono text-[13px] font-normal break-all';

/**
 * Στοιχεία του παραστατικού στο συρτάρι: ετικέτα → τιμή (όχι χρωματιστά κουτάκια), και τα σφάλματα του
 * παρόχου σε κόκκινη σημείωση. `showCode`: ο κωδικός τύπου (11.1) κάτω από το όνομα, όπως στον διαχειριστή.
 */
export const FiscalDocumentDetails: React.FC<{ doc: FiscalDocument; showCode?: boolean }> = ({ doc, showCode = false }) => {
  const { t } = useTranslation(['orders', 'settings', 'fiscalDocuments']);
  const table = documentTable(doc);
  const retries = Number(doc.retry_count) || 0;
  const items: KeyValueItem[] = [
    {
      id: 'type',
      label: t('orders:fiscalViewer.type'),
      value: (
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>{doc.document_type_name}</span>
          <FiscalTypeChip doc={doc} />
        </span>
      ),
      hint: showCode ? t('settings:ecr.typeCode', { code: doc.invoice_type_code }) : undefined,
    },
    {
      id: 'number',
      label: t('orders:fiscalViewer.seriesNumber'),
      value: <span className="tabular-nums">{doc.series} / {doc.document_number}</span>,
      hint: table ? t('orders:fiscalViewer.tableLabel', { table }) : undefined,
    },
    { id: 'date', label: t('orders:fiscalViewer.date'), value: fiscalWhen(doc.created_at, true) },
    { id: 'status', label: t('orders:fiscalViewer.status'), value: <FiscalStatusChip status={doc.status} /> },
    ...(doc.customer_name ? [{
      id: 'customer',
      label: t('orders:fiscalViewer.customer'),
      value: doc.customer_name,
      hint: doc.customer_vat_number ? `${t('orders:fiscalViewer.vatLabel')} ${doc.customer_vat_number}` : undefined,
    }] : []),
    { id: 'net', label: t('orders:fiscalViewer.netAmount'), value: formatMoney(Number(doc.total_net_amount)) },
    { id: 'vat', label: t('orders:fiscalViewer.vatAmount'), value: formatMoney(Number(doc.total_vat_amount)) },
    {
      id: 'total',
      label: t('orders:fiscalViewer.totalAmount'),
      value: <span className="font-semibold">{formatMoney(Number(doc.total_gross_amount))}</span>,
    },
    ...(doc.payment_method_name ? [{ id: 'payment', label: t('orders:fiscalViewer.paymentMethod'), value: doc.payment_method_name }] : []),
    ...(doc.mark ? [{ id: 'mark', label: 'MARK', value: <span className={MONO}>{doc.mark}</span> }] : []),
    ...(doc.oxygen_uid ? [{ id: 'uid', label: 'UID', value: <span className={MONO}>{doc.oxygen_uid}</span> }] : []),
  ];

  return (
    <>
      <KeyValue items={items} testId="fiscal-detail-values" />
      {(doc.error_message || doc.failure_message) && (
        <div className="mt-6">
          <Notice tone="danger" testId="fiscal-detail-error">
            <p className="font-semibold">
              {t('orders:fiscalViewer.errorDetails')}
              {retries > 0 && <span className="ml-2 font-normal">· {t('fiscalDocuments:detail.attempt', { count: retries })}</span>}
            </p>
            {doc.failure_message && (
              <div className="mt-2">
                <p className="text-xs font-medium">{t('orders:fiscalViewer.transmissionFailure')}</p>
                <p className={`mt-0.5 ${MONO}`}>{doc.failure_message}</p>
              </div>
            )}
            {doc.error_message && (
              <div className="mt-2">
                <p className="text-xs font-medium">{t('orders:fiscalViewer.errorMessage')}</p>
                <p className={`mt-0.5 ${MONO}`}>{doc.error_message}</p>
              </div>
            )}
            {doc.response_payload && (
              <div className="mt-2">
                <p className="text-xs font-medium">{t('orders:fiscalViewer.oxygenResponse')}</p>
                <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words text-xs">
                  {typeof doc.response_payload === 'string'
                    ? doc.response_payload
                    : JSON.stringify(doc.response_payload, null, 2)}
                </pre>
              </div>
            )}
          </Notice>
        </div>
      )}
    </>
  );
};

interface DocumentsByType {
  [code: string]: { count: number; total: number };
}

interface TrainingOrder {
  id: number;
  shortCode: string;
  total: number;
  paymentType: string;
  createdAt: string;
  cashierName: string | null;
  items: { name: string; quantity: number; unitPrice: number; totalPrice: number }[];
}

interface FiscalDocumentsViewerProps {
  isOpen: boolean;
  onClose: () => void;
}

const TYPE_FILTERS: Array<{ value: Exclude<DocumentFilterType, 'all'>; key: string }> = [
  { value: '11.1', key: 'receiptTypes11_1' },
  { value: '11.2', key: 'receiptTypes11_2' },
  { value: '1.1', key: 'receiptTypes1_1' },
  { value: '2.1', key: 'receiptTypes2_1' },
  { value: '8.6', key: 'receiptTypes8_6' },
  { value: '9.3', key: 'receiptTypes9_3' },
  { value: '11.4', key: 'receiptTypes11_4' },
  { value: '5.1', key: 'receiptTypes5_1' },
];

const STATUS_FILTERS: Array<{ value: string; key: string }> = [
  { value: 'success', key: 'statusSuccess' },
  { value: 'error', key: 'statusError' },
  { value: 'pending', key: 'statusPending' },
  { value: 'cancelled', key: 'statusCancelled' },
  { value: 'transmission_failure', key: 'statusTransmissionShort' },
];

// Η σύνοψη του server (όλα τα παραστατικά του φίλτρου, όχι μόνο της σελίδας) — σε μία γραμμή κειμένου.
const SUMMARY_ITEMS: Array<{ code: string; key: string }> = [
  { code: '11.1', key: 'summaryReceipts' },
  { code: '11.2', key: 'summaryServiceReceipts' },
  { code: '1.1', key: 'summaryInvoices' },
  { code: '2.1', key: 'summaryServiceInvoices' },
  { code: '8.6', key: 'summaryDeliveryNotes' },
  { code: '9.3', key: 'summaryDispatchNotes' },
  { code: 'cancel', key: 'summaryCancellations' },
];

const itemsSummary = (order: TrainingOrder): string => order.items
  .map(item => (item.quantity > 1 ? `${item.quantity}× ${item.name}` : item.name))
  .join(', ');

const ROW_BTN = 'inline-flex items-center gap-1.5 min-h-[36px] px-2.5 rounded-lg text-sm font-medium text-t2 hover:text-t1 '
  + 'hover:bg-surface-3 whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const FiscalDocumentsViewer: React.FC<FiscalDocumentsViewerProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation(['orders', 'common', 'fiscalDocuments', 'listPage']);
  const { store, employee } = useAuth();
  const { hasPermission } = usePermissions();
  const { showToast } = useToast();
  // Standalone fiscal-doc cancellation is store-admin only — the normal path is
  // to cancel the order (which auto-cancels its receipt).
  const canCancelFiscal = employee?.role === 'store_admin' || employee?.role === 'super_admin';
  const titleId = useId();
  const columns = useFiscalDocumentColumns();
  const rowLabel = useFiscalRowLabel();
  const titleOf = useFiscalTitle();

  const [documents, setDocuments] = useState<FiscalDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [summary, setSummary] = useState<DocumentsByType>({});

  const [documentFilter, setDocumentFilter] = useState<DocumentFilterType>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [selectedDocument, setSelectedDocument] = useState<FiscalDocument | null>(null);
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [reprintDone, setReprintDone] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });
  // Περίοδος: προεπιλογή «Σήμερα» (ημέρα Ελλάδας) — όπως πριν, που το «Από» έπαιρνε τη σημερινή μέρα όταν
  // άνοιγε το παράθυρο. Όσο δεν την άλλαξε ο χρήστης, ξαναγίνεται «Σήμερα» σε κάθε άνοιγμα (ταμείο ανοιχτό
  // από χθες = σημερινά, όχι χθεσινά).
  const [range, setRange] = useState<DateRangeValue>(() => presetRange('today'));
  const autoRange = useRef(true);
  const { from: dateFrom, to: dateTo } = range;

  // Training mode
  const [showTraining, setShowTraining] = useState(false);
  const [trainingOrders, setTrainingOrders] = useState<TrainingOrder[]>([]);
  const [trainingModeEnabled, setTrainingModeEnabled] = useState(false);
  const [trainingUnlocked, setTrainingUnlocked] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [issuingReceipt, setIssuingReceipt] = useState<number | null>(null);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [trainingError, setTrainingError] = useState<string | null>(null);
  const [selectedTraining, setSelectedTraining] = useState<TrainingOrder | null>(null);

  // Debounce search input — wait 300ms before triggering API request
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1); // Reset to first page on new search
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [documentFilter, statusFilter, dateFrom, dateTo]);

  // Clear the transient "printed" confirmation when switching documents
  useEffect(() => { setReprintDone(false); }, [selectedDocument?.id]);

  // Fetch documents with pagination
  const fetchDocuments = useCallback(async (page = currentPage) => {
    if (!store?.id) return;
    setLoading(true);
    setFetchError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String((page - 1) * PAGE_SIZE));
      if (documentFilter !== 'all') params.set('type', documentFilter);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (dateFrom) params.set('from', dateFrom);
      if (dateTo) params.set('to', dateTo);
      if (debouncedSearch) params.set('search', debouncedSearch);

      const response = await fetch(`${API_BASE_URL}/api/stores/${store.id}/fiscal/documents?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
        setTotalCount(data.total ?? data.documents?.length ?? 0);
        if (data.summary) setSummary(data.summary);
      } else {
        const errData = await response.json().catch(() => ({}));
        setFetchError(errData.message || t('orders:fiscalViewer.loadFailed'));
      }
    } catch (error) {
      console.error('Failed to fetch fiscal documents:', error);
      setFetchError(t('orders:fiscalViewer.connectionFailed'));
    } finally {
      setLoading(false);
    }
  }, [store?.id, documentFilter, statusFilter, dateFrom, dateTo, debouncedSearch, currentPage, t]);

  const fetchTrainingModeStatus = useCallback(async () => {
    if (!store?.id) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/fiscal/stores/${store.id}/cashier-document-types`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrainingModeEnabled(data.trainingModeEnabled || false);
      }
    } catch (error) {
      console.error('Failed to fetch training mode status:', error);
    }
  }, [store?.id]);

  const fetchTrainingOrders = useCallback(async () => {
    if (!store?.id) return;
    setTrainingLoading(true);
    setTrainingError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${store.id}/training`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrainingOrders(data.orders || []);
      } else {
        setTrainingError(t('orders:fiscalViewer.trainingLoadFailed'));
      }
    } catch (error) {
      setTrainingError(t('orders:fiscalViewer.connectionFailed'));
    } finally {
      setTrainingLoading(false);
    }
  }, [store?.id, t]);

  const handleRefreshClick = () => {
    if (showTraining) {
      fetchTrainingOrders();
      return;
    }
    fetchDocuments(currentPage);
    const next = refreshCount + 1;
    if (next >= 5 && trainingModeEnabled) {
      setTrainingUnlocked(true);
      setRefreshCount(0);
    } else {
      setRefreshCount(next);
    }
  };

  const handleIssueReceipt = async (orderId: number) => {
    setIssuingReceipt(orderId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${store?.id}/training/${orderId}/issue-receipt`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (response.ok) {
        setTrainingOrders(prev => prev.filter(o => o.id !== orderId));
        // Η παραγγελία δεν είναι πια εκπαιδευτική: το συρτάρι της κλείνει.
        setSelectedTraining(prev => (prev?.id === orderId ? null : prev));
      } else {
        const error = await response.json().catch(() => ({}));
        showToast('error', error.error || t('orders:fiscalViewer.issueFailed'));
      }
    } catch (error) {
      showToast('error', t('orders:fiscalViewer.connectionFailed'));
    } finally {
      setIssuingReceipt(null);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDocuments(currentPage);
      fetchTrainingModeStatus();
    } else {
      setShowTraining(false);
      setTrainingUnlocked(false);
      setRefreshCount(0);
    }
  }, [isOpen, fetchDocuments, currentPage, fetchTrainingModeStatus]);

  useEffect(() => {
    if (isOpen && showTraining) {
      fetchTrainingOrders();
    }
  }, [isOpen, showTraining, fetchTrainingOrders]);

  // Set default date filter to today (σε κάθε άνοιγμα, όσο ο χρήστης δεν διάλεξε άλλη περίοδο).
  useEffect(() => {
    if (!isOpen) return;
    setRange(prev => {
      if (!autoRange.current && prev.from) return prev;
      autoRange.current = true;
      const today = presetRange('today');
      return prev.from === today.from && prev.to === today.to ? prev : today;
    });
  }, [isOpen]);

  const changeRange = (next: DateRangeValue) => {
    autoRange.current = false;
    setRange(next);
  };

  // Retry handler
  const handleRetryDocument = async (documentId: number) => {
    setProcessingAction('retry');
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/fiscal/documents/${documentId}/retry`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (response.ok) {
        setSelectedDocument(null);
        fetchDocuments();
      } else {
        const error = await response.json();
        showToast('error', error.message || t('orders:fiscalViewer.resendFailed'));
      }
    } catch (error) {
      console.error('Error retrying document:', error);
      showToast('error', t('orders:fiscalViewer.resendFailed'));
    } finally {
      setProcessingAction(null);
    }
  };

  // Cancel handler
  const handleCancelDocument = async (documentId: number, invoiceTypeCode?: string) => {
    setConfirmModal({
      open: true,
      title: t('orders:fiscalViewer.cancelDocumentTitle'),
      // Το 9.3 ακυρώνεται στην ΑΑΔΕ χωρίς νέο παραστατικό: το «θα εκδοθεί ακυρωτικό» δεν ισχύει.
      message: t(invoiceTypeCode === '9.3' ? 'orders:fiscalViewer.cancelDispatchNoteMessage' : 'orders:fiscalViewer.cancelDocumentMessage'),
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        setProcessingAction('cancel');
        try {
          const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/fiscal/documents/${documentId}/cancel`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
          });
          if (response.ok) {
            const result = await response.json().catch(() => null);
            const printWarning = result?.success ? cancellationPrintWarning(result, t) : null;
            if (printWarning) showToast('warning', printWarning);
            setSelectedDocument(null);
            fetchDocuments();
          } else {
            const error = await response.json();
            showToast('error', error.message || t('orders:fiscalViewer.cancelFailed'));
          }
        } catch (error) {
          console.error('Error cancelling document:', error);
          showToast('error', t('orders:fiscalViewer.cancelFailed'));
        } finally {
          setProcessingAction(null);
        }
      }
    });
  };

  // Reprint handler — re-runs the original customer receipt/invoice print job
  const handleReprintDocument = async (documentId: number) => {
    setProcessingAction('reprint');
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/fiscal/documents/${documentId}/reprint`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (response.ok) {
        setReprintDone(true);
        setTimeout(() => setReprintDone(false), 2500);
      } else {
        const error = await response.json().catch(() => ({}));
        showToast('error', error.message || error.error || t('orders:fiscalViewer.reprintFailed'));
      }
    } catch (error) {
      console.error('Error reprinting document:', error);
      showToast('error', t('orders:fiscalViewer.connectionFailed'));
    } finally {
      setProcessingAction(null);
    }
  };

  // Το παράθυρο επιβεβαίωσης ζει μέσα στο συρτάρι: το Esc κλείνει ΠΡΩΤΑ αυτό.
  const closeDetail = () => {
    if (confirmModal.open) setConfirmModal(prev => ({ ...prev, open: false }));
    else setSelectedDocument(null);
  };

  // Χωρίς useMemo: το κουμπί της γραμμής καλεί το τρέχον handleIssueReceipt (και το issuingReceipt αλλάζει συχνά).
  const trainingColumns: Column<TrainingOrder>[] = [
    {
      key: 'order',
      header: t('fiscalDocuments:training.columns.order'),
      width: 'minmax(170px, 1fr)',
      stack: 'title',
      render: o => (
        <span className="inline-flex flex-wrap items-center gap-2">
          <span className="font-semibold tabular-nums">#{o.shortCode}</span>
          <StatusChip tone="warn">{t('orders:fiscalViewer.trainingBadge')}</StatusChip>
        </span>
      ),
      stackRender: o => `#${o.shortCode}`,
    },
    {
      key: 'when',
      header: t('fiscalDocuments:training.columns.when'),
      width: 'minmax(140px, 1fr)',
      render: o => (
        <>
          <span className="block tabular-nums whitespace-nowrap">{dayTime(o.createdAt)}</span>
          {o.cashierName && <span className="block text-xs text-t3 truncate">{o.cashierName.trim()}</span>}
        </>
      ),
      stackRender: o => [dayTime(o.createdAt), o.cashierName?.trim()].filter(Boolean).join(' · '),
    },
    {
      key: 'items',
      header: t('fiscalDocuments:training.columns.items'),
      width: 'minmax(180px, 2fr)',
      hideBelow: 'medium',
      render: o => <span className="block truncate text-t2" title={itemsSummary(o)}>{itemsSummary(o) || '—'}</span>,
    },
    {
      key: 'total',
      header: t('fiscalDocuments:training.columns.total'),
      width: '104px',
      align: 'right',
      stack: 'end',
      render: o => <span className="font-semibold">{formatMoney(o.total)}</span>,
      stackRender: o => formatMoney(o.total),
    },
    {
      key: 'issue',
      header: <span className="sr-only">{t('orders:fiscalViewer.issueReceipt')}</span>,
      width: '200px',
      align: 'right',
      hideBelow: 'narrow',
      // Η έκδοση με ένα πάτημα από τη γραμμή, όπως πριν· το κλικ στη γραμμή ανοίγει τα είδη.
      render: o => (
        <button type="button" className={ROW_BTN} onClick={() => handleIssueReceipt(o.id)} disabled={issuingReceipt !== null}>
          {issuingReceipt === o.id
            ? <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" aria-hidden="true" />
            : <CheckCircle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
          {issuingReceipt === o.id ? t('orders:fiscalViewer.issuingInProgress') : t('orders:fiscalViewer.issueReceipt')}
        </button>
      ),
    },
  ];

  if (!isOpen) return null;

  const busy = showTraining ? trainingLoading : loading;
  const filtersActive = documentFilter !== 'all' || statusFilter !== 'all' || !!searchTerm || !!dateFrom || !!dateTo;

  const summaryOf = (code: string) => (code === 'cancel'
    ? {
      count: (summary['11.4']?.count || 0) + (summary['5.1']?.count || 0),
      total: (summary['11.4']?.total || 0) + (summary['5.1']?.total || 0),
    }
    : { count: summary[code]?.count || 0, total: summary[code]?.total || 0 });

  const refreshButton = (
    <button type="button" className={GHOST_BTN} onClick={handleRefreshClick} disabled={busy}>
      <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} aria-hidden="true" />
      {t('common:refresh')}
    </button>
  );

  const documentsView = (
    <>
      <div className="pb-4">
        <Stats testId="fiscal-summary">
          {SUMMARY_ITEMS.map(({ code, key }) => {
            const { count, total } = summaryOf(code);
            return <Stat key={code} label={t(`orders:fiscalViewer.${key}`)} value={count} hint={formatMoney(total)} />;
          })}
        </Stats>
      </div>
      <ListToolbar
        search={{
          value: searchTerm,
          onChange: setSearchTerm,
          placeholder: t('orders:fiscalViewer.searchPlaceholder'),
          ariaLabel: t('fiscalDocuments:searchLabel'),
        }}
        filters={(
          <>
            <FilterSelect
              label={t('fiscalDocuments:typeFilter')}
              value={documentFilter === 'all' ? '' : documentFilter}
              onChange={v => setDocumentFilter((v || 'all') as DocumentFilterType)}
              allLabel={t('orders:fiscalViewer.allTypes')}
              options={TYPE_FILTERS.map(f => ({ value: f.value, label: t(`orders:fiscalViewer.${f.key}`) }))}
              testId="fiscal-type-filter"
            />
            <FilterSelect
              label={t('fiscalDocuments:statusFilter')}
              value={statusFilter === 'all' ? '' : statusFilter}
              onChange={v => setStatusFilter(v || 'all')}
              allLabel={t('orders:fiscalViewer.allStatuses')}
              options={STATUS_FILTERS.map(f => ({ value: f.value, label: t(`orders:fiscalViewer.${f.key}`) }))}
              testId="fiscal-status-filter"
            />
            <DateRange from={dateFrom} to={dateTo} onChange={changeRange} />
          </>
        )}
        actions={refreshButton}
        summary={!fetchError && !(loading && documents.length === 0) ? t('fiscalDocuments:count', { count: totalCount }) : null}
      />
      {fetchError ? (
        <LoadFailed message={fetchError} onRetry={() => fetchDocuments(currentPage)} />
      ) : (
        <DataTable
          columns={columns}
          rows={documents}
          rowKey={d => d.id}
          onRowClick={setSelectedDocument}
          selectedKey={selectedDocument?.id ?? null}
          loading={loading}
          rowLabel={rowLabel}
          empty={(
            <EmptyState
              icon={FileText}
              title={t('orders:fiscalViewer.noDocuments')}
              hint={filtersActive ? t('fiscalDocuments:emptyFilteredHint') : t('fiscalDocuments:emptyHint')}
            />
          )}
          footer={(
            <Pagination page={currentPage} pageSize={PAGE_SIZE} total={totalCount} onPage={setCurrentPage} disabled={loading} />
          )}
          testId="fiscal-table"
        />
      )}
    </>
  );

  const trainingView = (
    <>
      <ListToolbar
        actions={refreshButton}
        summary={!trainingError && !(trainingLoading && trainingOrders.length === 0)
          ? t('fiscalDocuments:training.count', { count: trainingOrders.length })
          : null}
      />
      {trainingError ? (
        <LoadFailed message={trainingError} onRetry={fetchTrainingOrders} />
      ) : (
        <DataTable
          columns={trainingColumns}
          rows={trainingOrders}
          rowKey={o => o.id}
          onRowClick={setSelectedTraining}
          selectedKey={selectedTraining?.id ?? null}
          loading={trainingLoading}
          rowLabel={o => t('fiscalDocuments:training.rowLabel', { code: o.shortCode, when: dayTime(o.createdAt) })}
          empty={(
            <EmptyState
              icon={GraduationCap}
              title={t('orders:fiscalViewer.noTrainingOrders')}
              hint={t('orders:fiscalViewer.trainingHint')}
            />
          )}
          testId="training-table"
        />
      )}
    </>
  );

  const doc = selectedDocument;
  const canReprint = !!doc && hasPermission('view_fiscal_documents') && doc.status === 'success'
    // Τα παραστατικά τραπεζιού δεν έχουν order_id (έχουν dine_in_session_id)· ως τις 6/10/2026 το κουμπί
    // έλειπε, ενώ το backend τα βρίσκει από το MARK.
    && !!(doc.order_id || doc.dine_in_session_id);
  const canRetry = !!doc && hasPermission('retry_fiscal_document') && RETRYABLE_STATUSES.includes(doc.status);
  const canCancel = !!doc && canCancelFiscal && isCancellable(doc);
  const training = selectedTraining;

  return (
    <>
      <div
        className="cashier-overlay fixed inset-0 z-[60] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)' }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="cashier-dialog bg-surface-0 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
          data-testid="fiscal-viewer"
        >
          <div className="cashier-dialog-header flex items-center justify-between gap-3 px-5 py-4 border-b border-line bg-surface-1 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-surface-3 flex items-center justify-center shrink-0">
                {showTraining
                  ? <GraduationCap className="w-5 h-5 text-t2" aria-hidden="true" />
                  : <FileText className="w-5 h-5 text-t2" aria-hidden="true" />}
              </div>
              <h2 id={titleId} className="text-lg font-bold text-t1 truncate">
                {showTraining ? t('orders:fiscalViewer.trainingOrders') : t('orders:fiscalViewer.title')}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-xl bg-surface-2 hover:bg-surface-3 flex items-center justify-center active:scale-95 transition-all"
              aria-label={t('common:close')}
              title={t('common:close')}
            >
              <X className="h-5 w-5 text-t2" aria-hidden="true" />
            </button>
          </div>

          <div className="overflow-y-auto p-6 flex-1">
            <ListPage embedded title={showTraining ? t('orders:fiscalViewer.trainingOrders') : t('orders:fiscalViewer.title')} testId="fiscal-viewer-page">
              {/* Οι «Εκπαιδευτικές» φαίνονται μόνο μετά από 5 × «Ανανέωση» (λειτουργία εκπαίδευσης ανοιχτή). */}
              {trainingUnlocked && (
                <div className="pb-4">
                  <div className="lp-seg" role="radiogroup" aria-label={t('fiscalDocuments:view')} data-testid="fiscal-view-switch">
                    <button type="button" role="radio" aria-checked={!showTraining} onClick={() => setShowTraining(false)}>
                      {t('orders:fiscalViewer.title')}
                    </button>
                    <button type="button" role="radio" aria-checked={showTraining} onClick={() => setShowTraining(true)}>
                      {t('orders:fiscalViewer.trainingTab')}
                      {trainingOrders.length > 0 && <span className="ml-1.5 tabular-nums text-t3">{trainingOrders.length}</span>}
                    </button>
                  </div>
                </div>
              )}
              {showTraining ? trainingView : documentsView}
            </ListPage>
          </div>
        </div>
      </div>

      <DetailPanel
        open={doc !== null}
        onClose={closeDetail}
        title={doc ? titleOf(doc) : ''}
        subtitle={doc ? fiscalWhen(doc.created_at) : undefined}
        footerStart={canCancel && doc ? (
          <button type="button" className={DANGER_GHOST_BTN} onClick={() => handleCancelDocument(doc.id, doc.invoice_type_code)} disabled={processingAction !== null}>
            {processingAction === 'cancel'
              ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              : <Ban className="w-4 h-4" aria-hidden="true" />}
            {t('orders:fiscalViewer.cancelDocument')}
          </button>
        ) : undefined}
        footer={doc ? (
          <>
            <button type="button" className={GHOST_BTN} onClick={() => setSelectedDocument(null)}>{t('listPage:close')}</button>
            {canReprint && (
              <button type="button" className={PRIMARY_BTN} onClick={() => handleReprintDocument(doc.id)} disabled={processingAction !== null}>
                {processingAction === 'reprint'
                  ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  : reprintDone ? <CheckCircle className="w-4 h-4" aria-hidden="true" /> : <Printer className="w-4 h-4" aria-hidden="true" />}
                {reprintDone ? t('orders:fiscalViewer.reprinted') : t('orders:fiscalViewer.reprint')}
              </button>
            )}
            {canRetry && (
              <button type="button" className={PRIMARY_BTN} onClick={() => handleRetryDocument(doc.id)} disabled={processingAction !== null}>
                {processingAction === 'retry'
                  ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  : <RotateCcw className="w-4 h-4" aria-hidden="true" />}
                {t('orders:fiscalViewer.resend')}
              </button>
            )}
          </>
        ) : undefined}
        testId="fiscal-detail"
      >
        {doc && <FiscalDocumentDetails doc={doc} />}
        {/* Μέσα στο συρτάρι: πάνω από αυτό (το παράθυρο του ταμείου είναι από κάτω) και με το Tab να φτάνει. */}
        <ConfirmModal
          isOpen={confirmModal.open}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(prev => ({ ...prev, open: false }))}
        />
      </DetailPanel>

      <DetailPanel
        open={training !== null}
        onClose={() => setSelectedTraining(null)}
        title={training ? `#${training.shortCode}` : ''}
        subtitle={training ? [dayTime(training.createdAt), training.cashierName?.trim()].filter(Boolean).join(' · ') : undefined}
        footer={training ? (
          <>
            <button type="button" className={GHOST_BTN} onClick={() => setSelectedTraining(null)}>{t('listPage:close')}</button>
            <button type="button" className={PRIMARY_BTN} onClick={() => handleIssueReceipt(training.id)} disabled={issuingReceipt !== null}>
              {issuingReceipt === training.id
                ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                : <CheckCircle className="w-4 h-4" aria-hidden="true" />}
              {issuingReceipt === training.id ? t('orders:fiscalViewer.issuingInProgress') : t('orders:fiscalViewer.issueReceipt')}
            </button>
          </>
        ) : undefined}
        testId="training-detail"
      >
        {training && (
          <>
            <KeyValue
              items={[
                { id: 'badge', label: t('fiscalDocuments:training.columns.order'), value: <StatusChip tone="warn">{t('orders:fiscalViewer.trainingBadge')}</StatusChip> },
                { id: 'when', label: t('fiscalDocuments:training.columns.when'), value: dayTime(training.createdAt) },
                ...(training.cashierName ? [{ id: 'cashier', label: t('fiscalDocuments:training.cashier'), value: training.cashierName.trim() }] : []),
                { id: 'total', label: t('fiscalDocuments:training.columns.total'), value: <span className="font-semibold">{formatMoney(training.total)}</span> },
              ]}
            />
            {training.items.length > 0 && (
              <div className="mt-6">
                <p className="xs-label">{t('fiscalDocuments:training.items')}</p>
                <KeyValue
                  testId="training-detail-items"
                  items={training.items.map((item, idx) => ({
                    id: String(idx),
                    label: item.quantity > 1 ? `${item.quantity}× ${item.name}` : item.name,
                    value: formatMoney(Number(item.totalPrice ?? 0)),
                  }))}
                />
              </div>
            )}
          </>
        )}
      </DetailPanel>
    </>
  );
};

export default FiscalDocumentsViewer;
